import { PersistedWorkflow } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import { asRecord, readString } from './components/utils';
import type { WorkflowNodeViewData } from './components/node/types';
import { graphNodeToViewData } from './workflow-graph-view-data';
import type { WorkflowGraphApiData } from './workflow-graph.types';

const { WorkflowNodeType } = PersistedWorkflow;

/**
 * How many levels of nesting the tree resolves before it stops descending.
 *
 * A guard, not a product limit: cycles are caught by ancestry (see
 * {@link buildNestedWorkflowTree}), so this only fires on genuinely deep chains,
 * where one more level of fetching costs a round trip and buys a graph nobody can
 * read anyway.
 */
export const NESTED_WORKFLOW_MAX_DEPTH = 10;

type GraphLike = Pick<WorkflowGraphApiData, 'nodes'>;

/** Whether this node runs another workflow as a sub-step. */
export function isRunWorkflowNode(node: WorkflowNodeViewData): boolean {
  return node.type === WorkflowNodeType.RunWorkflow;
}

/**
 * The target workflow id off a `run-workflow` node, or undefined for any other
 * node type and for a run-workflow node with nothing selected yet.
 *
 * Persisted as `data.targetWorkflowId` (see `workflowNodeInputSchema`).
 */
export function readNestedWorkflowReference(
  node: WorkflowNodeViewData
): string | undefined {
  if (!isRunWorkflowNode(node)) {
    return undefined;
  }
  const targetWorkflowId = readString(asRecord(node.data), 'targetWorkflowId');
  if (targetWorkflowId === undefined || targetWorkflowId.trim() === '') {
    return undefined;
  }
  return targetWorkflowId.trim();
}

/** One `run-workflow` node's pointer at the workflow it runs. */
export type NestedWorkflowReference = {
  readonly nodeId: string;
  readonly targetWorkflowId: string;
  /**
   * The target's name as the editor stamped it onto the node. Stale by nature
   * (renaming the target does not rewrite its callers), so it is only a fallback
   * for a child whose own graph has not arrived.
   */
  readonly nodeName: string | undefined;
};

/** Every workflow this graph runs as a sub-step, in graph node order. */
export function listNestedWorkflowReferences(
  graph: GraphLike
): NestedWorkflowReference[] {
  const references: NestedWorkflowReference[] = [];
  for (const node of graph.nodes) {
    const viewData = graphNodeToViewData(node);
    const targetWorkflowId = readNestedWorkflowReference(viewData);
    if (targetWorkflowId === undefined) {
      continue;
    }
    references.push({
      nodeId: node.id,
      targetWorkflowId,
      nodeName: readString(asRecord(node.data), 'name'),
    });
  }
  return references;
}

/**
 * What we know about one workflow in the tree. Ids absent from the source map
 * are treated as `loading` — the frontier is about to ask for them.
 */
export type NestedWorkflowSource =
  | { readonly status: 'missing' }
  | {
      readonly status: 'ready';
      readonly name: string | null;
      readonly graph: WorkflowGraphApiData;
    };

export type NestedWorkflowSources = ReadonlyMap<string, NestedWorkflowSource>;

/** The subtree hanging off one `run-workflow` node, or why there isn't one. */
export type NestedWorkflowChild =
  | { readonly kind: 'workflow'; readonly tree: NestedWorkflowTree }
  | { readonly kind: 'loading' | 'missing' | 'cycle' | 'too-deep' };

/** A parent/child link: the node that runs a workflow, and what it runs. */
export type NestedWorkflowLink = {
  /** Id of the `run-workflow` node inside the parent's own graph. */
  readonly nodeId: string;
  readonly targetWorkflowId: string;
  /** Best name available for the target, falling back to the node's label. */
  readonly name: string | null;
  readonly child: NestedWorkflowChild;
};

/**
 * One workflow in the tree: its own graph, exactly as its canvas would draw it,
 * plus the workflows it runs.
 *
 * The graph is kept whole rather than flattened to a step list, so the tree view
 * can lay each workflow out with the same graph-to-flow pass the editor canvas
 * uses and get the same cards, edges and trigger nodes for free.
 */
export type NestedWorkflowTree = {
  readonly workflowId: string;
  readonly name: string | null;
  /** 0 for the workflow the canvas is showing, 1 for what it runs, and so on. */
  readonly depth: number;
  readonly graph: WorkflowGraphApiData;
  readonly links: readonly NestedWorkflowLink[];
};

function resolveName(
  sources: NestedWorkflowSources,
  workflowId: string,
  fallback: string | undefined
): string | null {
  const source = sources.get(workflowId);
  if (source !== undefined && source.status === 'ready') {
    return source.name;
  }
  return fallback !== undefined && fallback.trim() !== ''
    ? fallback.trim()
    : null;
}

function childFor(params: {
  reference: NestedWorkflowReference;
  sources: NestedWorkflowSources;
  depth: number;
  maxDepth: number;
  ancestry: ReadonlySet<string>;
  name: string | null;
}): NestedWorkflowChild {
  const { reference, sources, depth, maxDepth, ancestry, name } = params;
  const { targetWorkflowId } = reference;

  if (ancestry.has(targetWorkflowId)) {
    return { kind: 'cycle' };
  }
  if (depth + 1 > maxDepth) {
    return { kind: 'too-deep' };
  }
  const source = sources.get(targetWorkflowId);
  if (source === undefined) {
    return { kind: 'loading' };
  }
  if (source.status === 'missing') {
    return { kind: 'missing' };
  }
  return {
    kind: 'workflow',
    tree: buildTree({
      workflowId: targetWorkflowId,
      name: source.name ?? name,
      graph: source.graph,
      sources,
      depth: depth + 1,
      maxDepth,
      ancestry,
    }),
  };
}

function buildTree(params: {
  workflowId: string;
  name: string | null;
  graph: WorkflowGraphApiData;
  sources: NestedWorkflowSources;
  depth: number;
  maxDepth: number;
  ancestry: ReadonlySet<string>;
}): NestedWorkflowTree {
  const { workflowId, name, graph, sources, depth, maxDepth } = params;
  const ancestry = new Set(params.ancestry).add(workflowId);

  const links = listNestedWorkflowReferences(graph).map((reference) => {
    const childName = resolveName(
      sources,
      reference.targetWorkflowId,
      reference.nodeName
    );
    return {
      nodeId: reference.nodeId,
      targetWorkflowId: reference.targetWorkflowId,
      name: childName,
      child: childFor({
        reference,
        sources,
        depth,
        maxDepth,
        ancestry,
        name: childName,
      }),
    };
  });

  return { workflowId, name, depth, graph, links };
}

/**
 * Resolves a workflow graph and everything it runs into one tree, keeping both
 * relationships the view needs: each workflow's own graph, and the parent/child
 * link pinned to the exact node that runs the child.
 *
 * A workflow that appears twice in one chain is cut with a `cycle` child rather
 * than recursed into, so `A -> B -> A` terminates. Repeats across *sibling*
 * branches are fine and expand normally — reusing one workflow in two places is
 * the point of nesting, not a loop.
 */
export function buildNestedWorkflowTree(params: {
  workflowId: string;
  name: string | null;
  graph: WorkflowGraphApiData;
  sources: NestedWorkflowSources;
  maxDepth?: number;
}): NestedWorkflowTree {
  return buildTree({
    workflowId: params.workflowId,
    name: params.name,
    graph: params.graph,
    sources: params.sources,
    depth: 0,
    maxDepth: params.maxDepth ?? NESTED_WORKFLOW_MAX_DEPTH,
    ancestry: new Set(),
  });
}

/**
 * Every workflow id the tree needs a graph for, given what has been fetched so
 * far — in discovery order, deduped.
 *
 * One call only sees as deep as the graphs already in `sources`, which is what
 * makes the recursion work in React: feed the result to `useQueries`, and each
 * batch that lands reveals the next level until the list stops growing.
 */
export function listNestedWorkflowIdsToFetch(params: {
  workflowId: string;
  graph: WorkflowGraphApiData;
  sources: NestedWorkflowSources;
  maxDepth?: number;
}): string[] {
  const maxDepth = params.maxDepth ?? NESTED_WORKFLOW_MAX_DEPTH;
  const wanted: string[] = [];
  const seen = new Set<string>();

  const walk = (
    graph: WorkflowGraphApiData,
    depth: number,
    ancestry: ReadonlySet<string>
  ): void => {
    if (depth >= maxDepth) {
      return;
    }
    for (const reference of listNestedWorkflowReferences(graph)) {
      const { targetWorkflowId } = reference;
      if (ancestry.has(targetWorkflowId)) {
        continue;
      }
      if (!seen.has(targetWorkflowId)) {
        seen.add(targetWorkflowId);
        wanted.push(targetWorkflowId);
      }
      const source = params.sources.get(targetWorkflowId);
      if (source === undefined || source.status !== 'ready') {
        continue;
      }
      walk(source.graph, depth + 1, new Set(ancestry).add(targetWorkflowId));
    }
  };

  walk(params.graph, 0, new Set([params.workflowId]));
  return wanted;
}

export type NestedWorkflowTreeSummary = {
  /** Nested workflows below the root, counting a workflow once per appearance. */
  readonly nestedCount: number;
  /** Deepest resolved level; 0 when the root nests nothing. */
  readonly maxDepth: number;
  /** Children still waiting on a fetch. */
  readonly loadingCount: number;
  /** Children whose workflow could not be read (deleted, or not visible). */
  readonly missingCount: number;
};

export function summarizeNestedWorkflowTree(
  tree: NestedWorkflowTree
): NestedWorkflowTreeSummary {
  let nestedCount = 0;
  let maxDepth = tree.depth;
  let loadingCount = 0;
  let missingCount = 0;

  const visit = (current: NestedWorkflowTree): void => {
    maxDepth = Math.max(maxDepth, current.depth);
    for (const link of current.links) {
      switch (link.child.kind) {
        case 'workflow':
          nestedCount += 1;
          visit(link.child.tree);
          break;
        case 'loading':
          loadingCount += 1;
          break;
        case 'missing':
          missingCount += 1;
          break;
        // Neither is a workflow the summary counts: a cycle is already counted
        // where it first appeared, and a too-deep child was never resolved. Named
        // rather than swept into a `default`, so a new child kind has to decide.
        case 'cycle':
        case 'too-deep':
          break;
        default:
          return Assert.unreachable(link.child);
      }
    }
  };

  visit(tree);
  return { nestedCount, maxDepth, loadingCount, missingCount };
}
